import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { CreateLeadInput, Lead, UpdateLeadInput } from "@school-sales-ai/shared";
import { buildQueryString } from "@/lib/query-string";
import {
  createLeadApi,
  deleteLeadApi,
  getDeletedLeadsApi,
  restoreLeadApi,
  updateLeadApi,
  api,
} from "@/lib/api";
import { useCrmStore } from "@/stores/crm.store";

export type LeadsResponse = { items: Lead[]; total: number };

/**
 * The paginated lead table. `enabled` is false while the merged page is in board
 * mode — both views read the same filters, so without it every keystroke in the
 * search box would fire two requests.
 */
export function useLeads({ enabled = true }: { enabled?: boolean } = {}) {
  const page = useCrmStore((s) => s.page);
  const pageSize = useCrmStore((s) => s.pageSize);
  const stages = useCrmStore((s) => s.stages);
  const channel = useCrmStore((s) => s.channel);
  const scoreMin = useCrmStore((s) => s.scoreMin);
  const scoreMax = useCrmStore((s) => s.scoreMax);
  const productIds = useCrmStore((s) => s.productIds);
  const cycleIds = useCrmStore((s) => s.cycleIds);
  const search = useCrmStore((s) => s.search);
  const from = useCrmStore((s) => s.from);
  const to = useCrmStore((s) => s.to);
  const labelIds = useCrmStore((s) => s.labelIds);
  const assigneeIds = useCrmStore((s) => s.assigneeIds);

  const filters = {
    page,
    pageSize,
    stages: stages.join(","),
    channel,
    scoreMin,
    scoreMax,
    productIds: productIds.join(","),
    cycleIds: cycleIds.join(","),
    search,
    from,
    to,
    labelIds: labelIds.join(","),
    assigneeIds: assigneeIds.join(","),
  };

  return useQuery({
    queryKey: ["leads", filters],
    queryFn: () =>
      api.get<LeadsResponse>(`/crm/leads?${buildQueryString(filters)}`),
    enabled,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLeadInput) => createLeadApi(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      void queryClient.invalidateQueries({ queryKey: ["product-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["cycle-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-stats"] });
    },
  });
}

/**
 * Fields that can move a lead between columns/lanes or change a server-side
 * aggregate. Only these are worth a refetch — a colour or a name is already
 * correct in every cache once `patchLeadInCaches` has written it.
 */
const STRUCTURAL_FIELDS = ["stage", "productId", "cycleId"] as const;

/** Query key prefixes whose entries hold `{ items: Lead[] }`. */
const LEAD_LIST_KEYS: QueryKey[] = [["pipeline"], ["pipeline-stage"], ["leads"]];

/**
 * Applies a partial lead update to every cache that holds that lead — the board
 * (`["pipeline"]`), an expanded column (`["pipeline-stage"]`), the table
 * (`["leads"]`) and the detail (`["lead", id]`) — without refetching any of them.
 *
 * Returns the snapshots taken before the write, so a failed request can put the
 * previous values back.
 */
function patchLeadInCaches(
  queryClient: QueryClient,
  leadId: string,
  patch: UpdateLeadInput,
): [QueryKey, unknown][] {
  const snapshots: [QueryKey, unknown][] = [];
  for (const queryKey of LEAD_LIST_KEYS) {
    snapshots.push(...queryClient.getQueriesData({ queryKey }));
    queryClient.setQueriesData<{ items: { id: string }[] }>({ queryKey }, (old) => {
      // `["leads", "deleted"]` matches the prefix too and holds a bare array.
      if (!old || !Array.isArray(old.items)) return old;
      return {
        ...old,
        items: old.items.map((l) => (l.id === leadId ? { ...l, ...patch } : l)),
      };
    });
  }
  snapshots.push([["lead", leadId], queryClient.getQueryData(["lead", leadId])]);
  queryClient.setQueryData<Lead>(["lead", leadId], (old) =>
    old ? { ...old, ...patch } : old,
  );
  return snapshots;
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      leadId,
      ...data
    }: { leadId: string } & UpdateLeadInput) => updateLeadApi(leadId, data),
    onMutate: async ({ leadId, ...data }) => {
      // Paint the change now. The card reads its colour from the board cache, and
      // the invalidation that used to be the only thing writing it re-fetched the
      // whole board — seconds on a real one, during which the click looked ignored.
      await Promise.all(
        LEAD_LIST_KEYS.map((queryKey) => queryClient.cancelQueries({ queryKey })),
      );
      return { snapshots: patchLeadInCaches(queryClient, leadId, data) };
    },
    onError: (_error, _vars, context) => {
      for (const [queryKey, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onSuccess: (updatedLead, { leadId }) => {
      // Merge, never replace. `["lead", id]` holds the detail shape (labels plus
      // the catalog joins); writing a response that happened to omit any of them
      // blanked those fields, and the lead page crashed on `labels.map`.
      queryClient.setQueryData<Lead>(["lead", leadId], (old) =>
        old ? { ...old, ...updatedLead } : updatedLead,
      );
    },
    onSettled: (_data, _error, { leadId: _leadId, ...data }) => {
      // A structural change needs the server's grouping and its joined catalog
      // names; anything else is done — refetching would cost a full board load
      // to learn what the cache already knows.
      if (!STRUCTURAL_FIELDS.some((field) => data[field] !== undefined)) return;
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline-stage"] });
      void queryClient.invalidateQueries({ queryKey: ["product-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["cycle-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-stats"] });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => deleteLeadApi(leadId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["leads", "deleted"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      void queryClient.invalidateQueries({ queryKey: ["product-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["cycle-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-stats"] });
    },
  });
}

export function useRestoreLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => restoreLeadApi(leadId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["leads", "deleted"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      void queryClient.invalidateQueries({ queryKey: ["product-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["cycle-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-stats"] });
    },
  });
}

export function useDeletedLeads({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["leads", "deleted"],
    queryFn: () => getDeletedLeadsApi(),
    enabled,
  });
}
